import doRequest from './request';

/**
 * Get documents from API and sets documents state
 *
 * @param {String} entity The entity name to fetch documents
 * @returns {Array||String} The array of documents or a string containing a response message
 */
const getDocuments = async (entity) => {
  const method = 'GET';
  const response = await createDocumentRequest(entity, { method });
  const documents = response.type === 'success' ? response.data : response;

  return documents;
};

/**
 * Check documents from API and sets documents state
 *
 * @param {String} entity The entity name to fetch documents
 * @returns {Array||String} The array of documents or a string containing a response message
 */
const checkDocuments = async (entity) => {
  const method = 'HEAD';
  const response = await createDocumentRequest(entity, { method });
  const documents = response.type === 'success' ? response.data : response;

  return documents;
};

/**
 * Function that sends an API request to create a document
 *
 * @param {Object} data The form data object
 * @param {Object} entity The entity name which the document must be created
 * @returns {Object||Boolean} The created role
 */
const createDocument = async (data, entity) => {
  const body = JSON.stringify(data);
  const method = 'POST';

  const createdDocument = await createDocumentRequest(entity, { method, body });

  return createdDocument;
};

/**
 * Function that sends an API request to update a document
 *
 * @param {Object} documentId The id of the document to be updated
 * @param {Object} data The document data
 * @param {Object} entity The entity name which the document must be updated
 * @returns {Object||Boolean} The created role
 */
const updateDocument = async (documentId, data, entity) => {
  const body = JSON.stringify(data);
  const method = 'PUT';

  const updatedDocument = await createDocumentRequest(entity, {
    documentId,
    method,
    body,
  });

  return updatedDocument;
};

/**
 * Function that sends an API request to update a document by path (no document ID required)
 *
 * @param {Object} data The document data (payload)
 * @param {String} entity The entity path to PUT to
 * @returns {Object||Boolean} The updated document
 */
const updateDocumentByPath = async (data, entity) => {
  const body = JSON.stringify(data);
  const method = 'PUT';

  const updatedDocument = await createDocumentRequest(entity, { method, body });

  return updatedDocument;
};

/**
 * Function that sends an API request to patch a document
 *
 * @param {Object} documentId The id of the document to be updated
 * @param {Object} data The document data
 * @param {Object} entity The entity name which the document must be updated
 * @returns {Object||Boolean} The created role
 */
const patchDocument = async (documentId, data, entity) => {
  const body = JSON.stringify(data);
  const method = 'PATCH';

  const patchedDocument = await createDocumentRequest(entity, {
    documentId,
    method,
    body,
  });

  return patchedDocument;
};

/**
 * Function that sends an API request to delete a document
 *
 * @param {Object} documentId The id of the document to be deleted
 * @param {Object} entity The entity name which the document must be deleted
 * @returns {Object||Boolean} The created role
 */
const deleteDocument = async (documentId, entity) => {
  const method = 'DELETE';

  const response = await createDocumentRequest(entity, {
    documentId,
    method,
  });

  return response;
};

/**
 * Generates the request URL from passed options
 * @param {String} entity The entity name
 * @param {Object} options The object containing defined request options
 *
 * @returns {String} The generated url
 */
const getUrlFromOptions = (entity, options) => {
  const { documentId, method } = options;

  switch (method) {
    case 'GET':
      return entity.indexOf('?') === -1 ? `${entity}/` : `${entity}`;

    case 'POST':
      return `${entity}`;

    case 'PUT':
      return documentId ? `${entity}/${documentId}/` : `${entity}/`;

    case 'PATCH':
      return documentId ? `${entity}/${documentId}/` : `${entity}/`;

    case 'DELETE':
      return `${entity}/${documentId}/`;

    default:
      return `${entity}/`;
  }
};

/**
 *
 * @param {*} entity the entity name to fetch documents
 * @param {*} options the options which the
 */

const createDocumentRequest = async (entity, options) => {
  const url = getUrlFromOptions(entity, options);

  try {
    const raw = await doRequest(url, options);
    const { status } = raw;
    const errorStatusesToReturnData = [400, 401, 403, 404, 410, 429];

    if (status === 204 || status === 202) {
      return { type: 'success', status };
    }

    if (status === 500 || status === 403) {
      return { type: 'error', status };
    }

    /** Check if response is JSON first */
    let parsed;
    try {
      parsed = await raw.json();
    } catch (error) {
      parsed = { isJSON: false };
    }

    /** Make sure to return data only on certain statuses */
    if (errorStatusesToReturnData.indexOf(status) !== -1) {
      return { type: 'error', status, data: parsed };
    }

    return { type: 'success', status, data: parsed };
  } catch (e) {
    // throw e
    return {};
  }
};

export {
  createDocumentRequest,
  createDocument,
  getDocuments,
  updateDocument,
  updateDocumentByPath,
  patchDocument,
  deleteDocument,
  checkDocuments,
};
